import { Injectable } from '@angular/core';
import {
	AbstractCRUDService,
	AlarisApiService,
	AlarisConfigService
} from '@campaign-portal/components-library';
import { ReadRequest, ReadResponse } from '@campaign-portal/namespace/common/implementations';
import { Filter, FilterType, RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import { ExportService } from '@helpers/api/export.service';
import { BehaviorSubject, catchError, map, Observable, of, tap } from 'rxjs';
import {SimulationFieldsService} from "./simulation.fields.service";
import {
	SimulationTaskCreateRequest,
	SimulationTaskCreateResponse,
	SimulationReadRequest,
	SimulationReadResponse,
	SimulationTaskDeleteResponse, SimulationTaskDeleteRequest
} from "@helpers/types/hlr";
import { Simulation, SiTask } from '@helpers/types/models';
import { InputComplexType } from '@campaign-portal/namespace/common/entityField';

@Injectable({
	providedIn: 'root'
})
export class SimulationService implements AbstractCRUDService {
	readonly entity = '';
	readonly title = '';

	readonly loading$ = new BehaviorSubject<boolean>(false);
	readonly refresh$ = new BehaviorSubject<boolean>(false);

	readonly create = this.update;

	lastSimulationTask: SiTask | null = null;

	constructor(
		private readonly api: AlarisApiService,
		private readonly exportService: ExportService,
		private readonly config: AlarisConfigService,
		private readonly fields: SimulationFieldsService
	) {}

	read(params: ReadRequest): Observable<ReadResponse<Simulation[]>> {
		const parsedParams = this.prepareParams(params);
		this.loading$.next(true);

		return this.api.loader<SimulationReadResponse>('Simulation.Read', parsedParams, this.loading$).pipe(
			map((resp) => ({
				Success: !!resp.data,
				Total: resp.length,
				Data: resp.data ?? []
			})),
			tap(() => this.refresh$.next(false)),
			catchError((err) => {
				console.error('Simulation read error', err);
				return of({ Success: false, Total: 0, Data: [] });
			}),
			tap(() => this.loading$.next(false))
		);
	}

	update(params: SimulationTaskCreateRequest): Observable<SimulationTaskCreateResponse> {
		this.loading$.next(true);
		return this.api.loader<SimulationTaskCreateResponse>('Simulation.Create', params, this.loading$).pipe(
			tap(() => this.loading$.next(false)),
			catchError((err) => {
				console.error('Simulation create error', err);
				return of({ Success: false, data: null } as SimulationTaskCreateResponse);
			})
		);
	}

	delete(params: SimulationTaskDeleteRequest): Observable<SimulationTaskDeleteResponse> {
		this.loading$.next(true);
		return this.api.loader<SimulationTaskDeleteResponse>('Simulation.Delete', params, this.loading$).pipe(
			tap(() => this.loading$.next(false)),
			catchError((err) => {
				console.error('Simulation delete error', err);
				return of({ Success: false, data: null } as SimulationTaskDeleteResponse);
			})
		);
	}

	export(total: number, params: RPCRequestParams): Observable<void> {
		const exportParams: RPCRequestParams = {
			...params,
			Paging: { Skip: 0, Take: total }
		};
		return this.exportService.export(
			this.config.api,
			'Simulation.Read',
			this.prepareParams(exportParams),
			[...this.fields.headers, {
				id: 8,
				name: 'simulation.client_product',
				variable: 'productId',
				filterType: FilterType.LIKE,
				required: false,
				type: InputComplexType.SELECT,
				data: [] }],
			'simulation'
		);
	}

	private prepareParams(params: RPCRequestParams): SimulationReadRequest {
		return {
			offset: params.Paging?.Skip ?? 0,
			limit: params.Paging?.Take ?? 25,
			active: '',
			direction: '',
			filter: params.Filters?.flatMap(this.parseFilterField) ?? []
		};
	}

	private parseFilterField(filter: Filter): { key: string; value: string | number }[] {
		const { Field, Value } = filter;

		switch (Field) {
			case 'id': {
				const normalizedId = String(Value).replace(/^si-/i, '');
				return [{ key: Field, value: normalizedId }];
			}
			case 'productName':
				if (typeof Value !== 'string') {
					throw new Error('Invalid productName filter value');
				}
				return [{ key: 'productName', value: Value }];

			case 'author':
				if (
					typeof Value === 'object' &&
					Value !== null &&
					'name' in Value
				) {
					const v = Value as { name: string; value: number };
					return [{ key: 'authorId', value: v.value.toString() }];
				}
				throw new Error('Invalid author filter value');

			case 'createdAt': {
				const result: { key: string; value: string }[] = [];
				const start = (Value as any)?.Start;
				const end = (Value as any)?.End;

				if (start) {
					result.push({ key: 'createdFrom', value: start });
				}

				if (end) {
					result.push({ key: 'createdTo', value: end });
				}

				return result;
			}

			default:
				return [{ key: Field, value: String(Value) }];
		}
	}

}

